import { TriggerType } from '@pushly/models/lib/enums/journeys'

/**
 * Journey status is the one condition whose value is a rule tree rather than a scalar.
 *
 * The engine stores it as a nested `campaigns` query — an inner condition group built from
 * three inner properties (`id`, `computed_trigger_type`, `status`) whose grouping and
 * operators encode a handful of user choices. The relationships between those choices are
 * subtle: negation lives on either the journey rule or the status rules but never both, and
 * a "not entered" state is the absence of a status group rather than a group full of
 * negations. Editing that tree in place, rule by rule, is what made the legacy widget hard
 * to reason about.
 *
 * This module reduces the tree to the four things a reader actually chooses — a selection
 * type, the journey or trigger it names, an equality operator, and a set of statuses — and
 * rebuilds the entire tree from those four on every change. Nothing patches the tree
 * incrementally: {@link deriveJourneyState} reads a stored tree into the derived state, and
 * {@link buildJourneyTree} is its inverse, producing the canonical tree the engine stores.
 * The two are exercised directly, so the coupling rules are testable without a DOM.
 */

/** Whether the condition matches any journey, a journey by trigger, or one specific journey. */
export type JourneySelectionType = 'any' | 'triggered' | 'specific'

/**
 * The reader's choices, reduced from the nested tree.
 *
 * `operator` is the equality the status match reads under; it is carried on the journey rule
 * when there is no status group (the "not entered" shape) and on the status rules otherwise,
 * which is the whole reason it lives here once rather than on each rule.
 *
 * A journey is held by id alone, with no cached display name. The name is the fetched list's to
 * supply: a stored condition arrives with only an id — the engine drops meta it does not model at
 * ingest — and a name carried through an in-session edit could only ever be one already in that
 * list, since picking it from the list is the only way to author one.
 */
export interface JourneyStatusState {
    selectionType: JourneySelectionType
    journeyId?: number
    triggerType?: string
    operator: 'eq' | 'neq'
    statuses: string[]
}

/** The statuses a journey membership can be matched on, in the order the reader sees them. */
export const JOURNEY_STATUSES: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'removed', label: 'Removed' },
]

/**
 * The triggers a "journey with trigger" condition can name.
 *
 * The values are the `TriggerType` enum's own, so the wire carries exactly what the enum
 * resolves to — the same string the legacy widget stored, since it read the value off the
 * same enum. The labels are the legacy widget's, verbatim.
 */
export const JOURNEY_TRIGGERS: ReadonlyArray<{ value: string; label: string }> = [
    { value: TriggerType.SUBSCRIBED, label: 'Subscribing to Push Notifications' },
    { value: TriggerType.ABANDONED_CART, label: 'Abandoning Cart' },
    { value: TriggerType.ABANDONED_VIEW, label: 'Abandoning Browse' },
]

type Dict = Record<string, unknown>

const ID_PROPERTY = 'id'
const TRIGGER_PROPERTY = 'computed_trigger_type'
const STATUS_PROPERTY = 'status'

interface Leaf {
    property: string
    operator: string
    value: unknown
    meta?: Dict
}

function isDict(value: unknown): value is Dict {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** The child array of a group dict (`and`/`or`/`and_not`), or empty when it is not a group. */
function groupChildren(group: unknown): Dict[] {
    if (!isDict(group)) return []
    const arr = group.and ?? group.or ?? group.and_not
    return Array.isArray(arr) ? (arr.filter(isDict) as Dict[]) : []
}

/**
 * A single leaf condition `{ property: { operator: value, meta? } }` read into its parts, or
 * `null` when the node is a group or otherwise not a single-property leaf.
 */
function readLeaf(node: Dict): Leaf | null {
    const keys = Object.keys(node)
    if (keys.length !== 1) return null
    const property = keys[0]
    const body = node[property]
    if (!isDict(body)) return null
    const operator = Object.keys(body).find((k) => k !== 'meta')
    if (operator === undefined) return null
    return { property, operator, value: body[operator], meta: isDict(body.meta) ? body.meta : undefined }
}

/** Every leaf condition in the tree, one level of `or` groups below the root, flattened. */
function collectLeaves(group: unknown): Leaf[] {
    const leaves: Leaf[] = []
    for (const child of groupChildren(group)) {
        for (const node of groupChildren(child)) {
            const leaf = readLeaf(node)
            if (leaf) leaves.push(leaf)
        }
    }
    return leaves
}

function normalizeOperator(operator: string): 'eq' | 'neq' {
    return operator === 'neq' ? 'neq' : 'eq'
}

/**
 * Every status named, which is how a total match and a total negation are both spelled.
 *
 * Exported because the collapsed row has to recognize the same condition the tree encodes: a negated
 * specific journey with every status named is stored as the journey rule alone, and a row that read
 * it as a status match would describe the opposite of what the segment matches.
 */
export function allStatusesSelected(statuses: string[]): boolean {
    return JOURNEY_STATUSES.every((option) => statuses.includes(option.value))
}

/**
 * Reads a stored inner group into the derived state.
 *
 * The reductions mirror how the tree encodes each choice: the selection type is which kind of
 * front rule is present, the operator is read off the status rules when they exist and off the
 * journey rule otherwise, and an absent status group under a negation reads back as all
 * statuses selected — the "not entered" shape, where the negation is carried by the journey
 * rule alone.
 */
export function deriveJourneyState(group: unknown): JourneyStatusState {
    const leaves = collectLeaves(group)
    const idLeaves = leaves.filter((leaf) => leaf.property === ID_PROPERTY)
    const triggerLeaves = leaves.filter((leaf) => leaf.property === TRIGGER_PROPERTY)
    const statusLeaves = leaves.filter((leaf) => leaf.property === STATUS_PROPERTY)

    let selectionType: JourneySelectionType = 'any'
    if (idLeaves.length > 0) selectionType = 'specific'
    else if (triggerLeaves.length > 0) selectionType = 'triggered'

    // The status rules own the operator when present; the journey rule owns it otherwise, which
    // is where a "not entered" condition keeps its negation.
    let operator: 'eq' | 'neq' = 'eq'
    if (idLeaves.length > 0) operator = normalizeOperator(idLeaves[0].operator)
    if (statusLeaves.length > 0) operator = normalizeOperator(statusLeaves[0].operator)

    const statuses =
        statusLeaves.length > 0
            ? statusLeaves.map((leaf) => String(leaf.value))
            : operator === 'neq'
              ? JOURNEY_STATUSES.map((option) => option.value)
              : []

    const state: JourneyStatusState = { selectionType, operator, statuses }

    if (idLeaves.length > 0) {
        const idLeaf = idLeaves[0]
        if (idLeaf.value !== undefined && idLeaf.value !== null) state.journeyId = Number(idLeaf.value)
    }

    if (triggerLeaves.length > 0) {
        const triggerValue = triggerLeaves[0].value
        if (triggerValue !== undefined && triggerValue !== null) state.triggerType = String(triggerValue)
    }

    return state
}

/**
 * Rebuilds the canonical inner group from the derived state — the inverse of
 * {@link deriveJourneyState}.
 *
 * The whole tree is produced fresh from the four choices, never patched. The front group holds
 * the journey or trigger rule; the status group, when present, holds one rule per status. The
 * one coupling that shapes the structure is negation: a negated match of all or no statuses on
 * a specific journey is stored as the journey rule carrying `neq` with no status group at all
 * (the "not entered" shape), because a status group cannot both exist and be a total negation.
 * Every other negation is carried by the status rules, which forces the journey rule back to
 * `eq` — so selecting a subset of statuses under `neq` moves the negation off the journey.
 *
 * A negation with no status chosen is only representable on a specific journey, where the journey
 * rule carries it. Nothing materializes the full status set to stand in for it elsewhere: excluding
 * all three statuses excludes every membership there is, so the tree would match nobody. An empty
 * group is the honest result, and the builder reports it as the incomplete condition it is.
 *
 * An incomplete choice is represented rather than omitted — a specific journey not yet picked
 * still emits its `id` rule, holding the reader's selection while the builder reports the rule as
 * missing a value. Dropping the rule instead would leave a tree that reads back as some other
 * choice entirely, moving the reader's selection under them instead of telling them what is left
 * to fill in.
 */
export function buildJourneyTree(state: JourneyStatusState): Dict {
    const groups: Dict[] = []
    const statuses = state.statuses

    const negationOnJourney =
        state.operator === 'neq' &&
        state.selectionType === 'specific' &&
        (statuses.length === 0 || allStatusesSelected(statuses))

    if (state.selectionType === 'specific') {
        groups.push({ or: [{ [ID_PROPERTY]: { [negationOnJourney ? 'neq' : 'eq']: state.journeyId } }] })
    } else if (state.selectionType === 'triggered') {
        groups.push({ or: [{ [TRIGGER_PROPERTY]: { eq: state.triggerType } }] })
    }

    if (!negationOnJourney && statuses.length > 0) {
        groups.push({
            [statusJoinerFor(state.operator, statuses.length)]: statuses.map((status) => ({
                [STATUS_PROPERTY]: { [state.operator]: status },
            })),
        })
    }

    return { and: groups }
}

/**
 * How the status rules of a set are joined, which the equality decides.
 *
 * A membership carries exactly one status, so a set of them is only ever matched one rule at a time
 * and the join is what says whether that counts for or against the condition. Under `eq` the reader
 * means any of the chosen statuses, which is `or`. Under `neq` they mean none of them, and that is
 * `and`: `or` there asks whether the status differs from *at least one* of the chosen values, which
 * anything with a journey satisfies as soon as two are chosen — a condition that reads as an
 * exclusion and matches everyone.
 *
 * A lone rule is joined with `or` under either equality. The two are identical for one rule, and
 * keeping `or` leaves every stored single-status condition byte-identical, so the change to what is
 * stored reaches only the sets that were being evaluated wrongly.
 */
function statusJoinerFor(operator: 'eq' | 'neq', count: number): 'and' | 'or' {
    return operator === 'neq' && count > 1 ? 'and' : 'or'
}
